import { type FetchResult } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useMemo, useState } from "react";
import PageLayout from "../../components/common/page-layout";
import {
  DocumentationAdminTopicsDocument,
  DocumentationItemStatus,
  useDocumentationAdminDeleteItemMutation,
  useDocumentationAdminDeleteTopicMutation,
  useDocumentationAdminReorderMutation,
  useDocumentationAdminTopicsQuery,
  useDocumentationAdminUpdateItemMutation,
} from "../../generated/graphql";
import { useConfirmDialog } from "../api-keys-admin/components/confirm-dialog";
import useIsInternalAdmin from "../api-keys-admin/hooks/use-is-internal-admin";
import { throwOnGraphqlErrors } from "../api-keys-admin/throw-on-graphql-errors";
import ItemDialog from "./components/item-dialog";
import TopicDialog, { type TopicDialogTopic } from "./components/topic-dialog";
import { moveUuid } from "./move-uuid";

const PAGE_TITLE = "Documentation";
const BYTES_PER_KB = 1024;

const styles = {
  layout: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
    gap: 3,
    alignItems: "start",
  } satisfies SxProps<Theme>,
  panelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: 2,
    py: 1.5,
  } satisfies SxProps<Theme>,
  centered: {
    display: "flex",
    justifyContent: "center",
    p: 4,
  } satisfies SxProps<Theme>,
  errorAlert: { mb: 2 } satisfies SxProps<Theme>,
  rowActions: { whiteSpace: "nowrap" } satisfies SxProps<Theme>,
};

type ItemDialogState = { readonly itemUuid: string | null } | null;

/**
 * Authoring screen for the help-document library shown to customers on
 * Settings > Documentation. Topics on the left; the selected topic's
 * documents on the right, each uploaded as a self-contained HTML file.
 * New documents are drafts until published from here.
 *
 * Writes go to the environment picked in the header, like every other tool.
 */
const DocumentationAdminPage = () => {
  const { canAccess, loading: permissionsLoading } = useIsInternalAdmin();
  const { confirm, confirmDialog } = useConfirmDialog();
  const [selectedTopicUuid, setSelectedTopicUuid] = useState<string | null>(
    null,
  );
  const [topicDialog, setTopicDialog] = useState<{
    readonly topic: TopicDialogTopic | null;
  } | null>(null);
  const [itemDialog, setItemDialog] = useState<ItemDialogState>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, loading, error } = useDocumentationAdminTopicsQuery({
    skip: !canAccess,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });
  const topics = useMemo(() => data?.documentationAdminTopics ?? [], [data]);
  const selectedTopic =
    topics.find((topic) => topic.uuid === selectedTopicUuid) ?? topics[0];
  const topicOptions = useMemo(
    () => topics.map(({ uuid, title }) => ({ uuid, title })),
    [topics],
  );

  const refetchQueries = [DocumentationAdminTopicsDocument];
  const [reorder] = useDocumentationAdminReorderMutation({ refetchQueries });
  const [deleteTopic] = useDocumentationAdminDeleteTopicMutation({
    refetchQueries,
  });
  const [deleteItem] = useDocumentationAdminDeleteItemMutation({
    refetchQueries,
  });
  const [updateItem] = useDocumentationAdminUpdateItemMutation({
    refetchQueries,
  });

  /** Runs one mutation, surfacing its GraphQL errors (the client does not throw). */
  const run = async (action: () => Promise<FetchResult<unknown>>) => {
    setActionError(null);
    try {
      throwOnGraphqlErrors(await action());
    } catch (runError) {
      setActionError(
        runError instanceof Error ? runError.message : String(runError),
      );
    }
  };

  const moveTopic = async (uuid: string, step: -1 | 1) =>
    run(async () =>
      reorder({
        variables: {
          input: {
            uuids: moveUuid(
              topics.map((topic) => topic.uuid),
              uuid,
              step,
            ),
          },
        },
      }),
    );

  const moveItem = (uuid: string, step: -1 | 1) => {
    if (selectedTopic === undefined) {
      return;
    }
    void run(async () =>
      reorder({
        variables: {
          input: {
            topicUuid: selectedTopic.uuid,
            uuids: moveUuid(
              selectedTopic.items.map((item) => item.uuid),
              uuid,
              step,
            ),
          },
        },
      }),
    );
  };

  const removeTopic = async (topic: { uuid: string; title: string }) => {
    try {
      await confirm({
        title: `Delete "${topic.title}"?`,
        description: "Only an empty topic can be deleted.",
        confirmationText: "Delete",
      });
    } catch {
      return;
    }
    await run(async () => deleteTopic({ variables: { uuid: topic.uuid } }));
  };

  const removeItem = async (item: { uuid: string; title: string }) => {
    try {
      await confirm({
        title: `Delete "${item.title}"?`,
        description:
          "Customers lose access immediately, and other documents linking to it show it as unavailable. Unpublishing keeps the file instead.",
        confirmationText: "Delete",
      });
    } catch {
      return;
    }
    await run(async () => deleteItem({ variables: { uuid: item.uuid } }));
  };

  const setStatus = (uuid: string, status: DocumentationItemStatus) => {
    void run(async () =>
      updateItem({ variables: { input: { uuid, status } } }),
    );
  };

  if (permissionsLoading) {
    return (
      <PageLayout maxWidth={false} title={PAGE_TITLE}>
        <Box sx={styles.centered}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (!canAccess) {
    return (
      <PageLayout maxWidth={false} title={PAGE_TITLE}>
        <Alert severity="error">
          This tool requires the INTERNAL_TOOLS permission. Ask an admin to
          grant access.
        </Alert>
      </PageLayout>
    );
  }

  return (
    <PageLayout maxWidth={false} title={PAGE_TITLE}>
      {error !== undefined && (
        <Alert severity="error" sx={styles.errorAlert}>
          {error.message}
        </Alert>
      )}
      {actionError !== null && (
        <Alert
          severity="error"
          sx={styles.errorAlert}
          onClose={() => {
            setActionError(null);
          }}
        >
          {actionError}
        </Alert>
      )}
      <Box sx={styles.layout}>
        <Paper variant="outlined">
          <Box sx={styles.panelHeader}>
            <Typography variant="subtitle1">Topics</Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setTopicDialog({ topic: null });
              }}
            >
              New topic
            </Button>
          </Box>
          {loading && topics.length === 0 ? (
            <Box sx={styles.centered}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <List dense disablePadding>
              {topics.map((topic, index) => (
                <ListItemButton
                  key={topic.uuid}
                  selected={topic.uuid === selectedTopic?.uuid}
                  onClick={() => {
                    setSelectedTopicUuid(topic.uuid);
                  }}
                >
                  <ListItemText
                    primary={topic.title}
                    secondary={`${topic.items.length} document(s)`}
                  />
                  <IconButton
                    size="small"
                    aria-label={`Move ${topic.title} up`}
                    disabled={index === 0}
                    onClick={(event) => {
                      event.stopPropagation();
                      void moveTopic(topic.uuid, -1);
                    }}
                  >
                    <ArrowUpwardIcon fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={`Move ${topic.title} down`}
                    disabled={index === topics.length - 1}
                    onClick={(event) => {
                      event.stopPropagation();
                      void moveTopic(topic.uuid, 1);
                    }}
                  >
                    <ArrowDownwardIcon fontSize="inherit" />
                  </IconButton>
                </ListItemButton>
              ))}
              {topics.length === 0 && error === undefined && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={styles.panelHeader}
                >
                  No topics yet.
                </Typography>
              )}
            </List>
          )}
        </Paper>

        <Paper variant="outlined">
          {selectedTopic === undefined ? (
            <Box sx={styles.centered}>
              <Typography color="text.secondary">
                Create a topic to start adding documents.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={styles.panelHeader}>
                <Box>
                  <Typography variant="subtitle1">
                    {selectedTopic.title}
                  </Typography>
                  {selectedTopic.description !== null &&
                    selectedTopic.description !== undefined && (
                      <Typography variant="body2" color="text.secondary">
                        {selectedTopic.description}
                      </Typography>
                    )}
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    startIcon={<EditOutlinedIcon />}
                    onClick={() => {
                      setTopicDialog({ topic: selectedTopic });
                    }}
                  >
                    Edit topic
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    disabled={selectedTopic.items.length > 0}
                    onClick={() => {
                      void removeTopic(selectedTopic);
                    }}
                  >
                    Delete topic
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setItemDialog({ itemUuid: null });
                    }}
                  >
                    New document
                  </Button>
                </Stack>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Slug</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Size</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedTopic.items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No documents in this topic.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {selectedTopic.items.map((item, index) => {
                    const isPublished =
                      item.status === DocumentationItemStatus.Published;
                    return (
                      <TableRow key={item.uuid} hover>
                        <TableCell>{item.title}</TableCell>
                        <TableCell>{item.code ?? "—"}</TableCell>
                        <TableCell>{item.slug}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={isPublished ? "success" : "default"}
                            label={isPublished ? "Published" : "Draft"}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {`${Math.round(item.htmlSizeBytes / BYTES_PER_KB)} KB`}
                        </TableCell>
                        <TableCell>
                          {new Date(String(item.updatedAt)).toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={styles.rowActions}>
                          <Button
                            size="small"
                            onClick={() => {
                              setStatus(
                                item.uuid,
                                isPublished
                                  ? DocumentationItemStatus.Draft
                                  : DocumentationItemStatus.Published,
                              );
                            }}
                          >
                            {isPublished ? "Unpublish" : "Publish"}
                          </Button>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              aria-label={`Edit ${item.title}`}
                              onClick={() => {
                                setItemDialog({ itemUuid: item.uuid });
                              }}
                            >
                              <EditOutlinedIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            aria-label={`Move ${item.title} up`}
                            disabled={index === 0}
                            onClick={() => {
                              moveItem(item.uuid, -1);
                            }}
                          >
                            <ArrowUpwardIcon fontSize="inherit" />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label={`Move ${item.title} down`}
                            disabled={index === selectedTopic.items.length - 1}
                            onClick={() => {
                              moveItem(item.uuid, 1);
                            }}
                          >
                            <ArrowDownwardIcon fontSize="inherit" />
                          </IconButton>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              aria-label={`Delete ${item.title}`}
                              onClick={() => {
                                void removeItem(item);
                              }}
                            >
                              <DeleteOutlineIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          )}
        </Paper>
      </Box>

      <TopicDialog
        open={topicDialog !== null}
        topic={topicDialog?.topic ?? null}
        onClose={() => {
          setTopicDialog(null);
        }}
        onSaved={(uuid) => {
          setTopicDialog(null);
          setSelectedTopicUuid(uuid);
        }}
      />
      {selectedTopic !== undefined && (
        <ItemDialog
          open={itemDialog !== null}
          itemUuid={itemDialog?.itemUuid ?? null}
          defaultTopicUuid={selectedTopic.uuid}
          topics={topicOptions}
          onClose={() => {
            setItemDialog(null);
          }}
        />
      )}
      {confirmDialog}
    </PageLayout>
  );
};

export default DocumentationAdminPage;
